import { getFallbackVerse } from './bible-fallback';

export interface FetchedVerse {
  reference: string;
  text: string;
  isFallback?: boolean;
}

const HELLOAO_API_BASE = 'https://bible.helloao.org/api';

const LOCALE_TO_TRANSLATION: Record<string, string> = {
  pt: 'por_bsl',
  'pt-PT': 'por_bsl',
  en: 'BSB',
  es: 'spa_r09',
  fr: 'fra_lsg',
  de: 'deu_l12',
  it: 'ita_riv',
  zh: 'cmn_cu1',
  ar: 'ARBNAV',
};

const LOCALES_WITHOUT_API_SUPPORT = new Set(['ja']);

const BOOK_CODES: Record<string, string> = {
  genesis: 'GEN',
  exodus: 'EXO',
  leviticus: 'LEV',
  numbers: 'NUM',
  deuteronomy: 'DEU',
  joshua: 'JOS',
  judges: 'JDG',
  ruth: 'RUT',
  '1 samuel': '1SA',
  '2 samuel': '2SA',
  '1 kings': '1KI',
  '2 kings': '2KI',
  '1 chronicles': '1CH',
  '2 chronicles': '2CH',
  ezra: 'EZR',
  nehemiah: 'NEH',
  esther: 'EST',
  job: 'JOB',
  psalm: 'PSA',
  psalms: 'PSA',
  proverbs: 'PRO',
  ecclesiastes: 'ECC',
  'song of solomon': 'SNG',
  'song of songs': 'SNG',
  isaiah: 'ISA',
  jeremiah: 'JER',
  lamentations: 'LAM',
  ezekiel: 'EZK',
  daniel: 'DAN',
  hosea: 'HOS',
  joel: 'JOL',
  amos: 'AMO',
  obadiah: 'OBA',
  jonah: 'JON',
  micah: 'MIC',
  nahum: 'NAM',
  habakkuk: 'HAB',
  zephaniah: 'ZEP',
  haggai: 'HAG',
  zechariah: 'ZEC',
  malachi: 'MAL',
  matthew: 'MAT',
  mark: 'MRK',
  luke: 'LUK',
  john: 'JHN',
  acts: 'ACT',
  romans: 'ROM',
  '1 corinthians': '1CO',
  '2 corinthians': '2CO',
  galatians: 'GAL',
  ephesians: 'EPH',
  philippians: 'PHP',
  colossians: 'COL',
  '1 thessalonians': '1TH',
  '2 thessalonians': '2TH',
  '1 timothy': '1TI',
  '2 timothy': '2TI',
  titus: 'TIT',
  philemon: 'PHM',
  hebrews: 'HEB',
  james: 'JAS',
  '1 peter': '1PE',
  '2 peter': '2PE',
  '1 john': '1JN',
  '2 john': '2JN',
  '3 john': '3JN',
  jude: 'JUD',
  revelation: 'REV',
};

interface ParsedReference {
  book: string;
  chapter: number;
  verses: number[];
}

function parseReference(reference: string): ParsedReference | null {
  const match = reference.match(/^(.+?)\s+(\d+):(.+)$/);
  if (!match) return null;

  const [, bookRaw, chapterStr, versesPart] = match;
  const bookCode = BOOK_CODES[bookRaw.toLowerCase().trim()];
  if (!bookCode) return null;

  const chapter = parseInt(chapterStr, 10);
  const verses: number[] = [];

  const parts = versesPart.split(',').map((p) => p.trim());
  for (const part of parts) {
    if (part.includes('-')) {
      const [start, end] = part.split('-').map((n) => parseInt(n.trim(), 10));
      for (let i = start; i <= end; i++) {
        verses.push(i);
      }
    } else {
      verses.push(parseInt(part, 10));
    }
  }

  return { book: bookCode, chapter, verses };
}

interface VerseContent {
  type: string;
  number?: number;
  content?: Array<{ text?: string }>;
}

interface ChapterResponse {
  chapter: {
    content: VerseContent[];
  };
}

function extractVerseText(content: Array<{ text?: string }>): string {
  return content
    .filter((c) => c.text)
    .map((c) => c.text)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const chapterCache = new Map<string, ChapterResponse>();

async function fetchChapter(
  translation: string,
  book: string,
  chapter: number
): Promise<ChapterResponse | null> {
  const cacheKey = `${translation}/${book}/${chapter}`;

  if (chapterCache.has(cacheKey)) {
    return chapterCache.get(cacheKey)!;
  }

  try {
    const url = `${HELLOAO_API_BASE}/${translation}/${book}/${chapter}.json`;
    const response = await fetch(url);

    if (!response.ok) {
      console.warn(`[HelloAO] Failed to fetch ${cacheKey}: ${response.status}`);
      return null;
    }

    const data: ChapterResponse = await response.json();
    chapterCache.set(cacheKey, data);
    return data;
  } catch (error) {
    console.error(`[HelloAO] Error fetching ${cacheKey}:`, error);
    return null;
  }
}

async function fetchVerseFromHelloAO(
  reference: string,
  locale: string
): Promise<FetchedVerse | null> {
  const parsed = parseReference(reference);
  if (!parsed) {
    console.warn(`[HelloAO] Could not parse reference: ${reference}`);
    return null;
  }

  const translation =
    LOCALE_TO_TRANSLATION[locale] ||
    LOCALE_TO_TRANSLATION[locale.split('-')[0]] ||
    LOCALE_TO_TRANSLATION.en;

  const chapterData = await fetchChapter(translation, parsed.book, parsed.chapter);
  if (!chapterData) return null;

  const verseTexts: string[] = [];

  for (const verseNum of parsed.verses) {
    const verse = chapterData.chapter.content.find(
      (c) => c.type === 'verse' && c.number === verseNum
    );

    if (verse?.content) {
      const text = extractVerseText(verse.content);
      if (text) verseTexts.push(text);
    }
  }

  if (verseTexts.length === 0) return null;

  return {
    reference,
    text: verseTexts.join(' '),
  };
}

async function fetchVerseWithFallback(
  ref: string,
  locale: string
): Promise<FetchedVerse | null> {
  const baseLocale = locale.split('-')[0];

  if (LOCALES_WITHOUT_API_SUPPORT.has(baseLocale)) {
    const fallbackText = getFallbackVerse(ref, locale);
    if (fallbackText) {
      return { reference: ref, text: fallbackText, isFallback: true };
    }
    return null;
  }

  let verse = await fetchVerseFromHelloAO(ref, locale);

  if (!verse) {
    const fallbackText = getFallbackVerse(ref, locale);
    if (fallbackText) {
      console.log(`[Bible API] Using fallback for ${ref}`);
      verse = { reference: ref, text: fallbackText, isFallback: true };
    }
  }

  return verse?.text ? verse : null;
}

export async function fetchMultipleVerses(
  references: string[],
  locale: string = 'en'
): Promise<FetchedVerse[]> {
  if (references.length === 0) return [];

  console.log(`[Bible API] Fetching ${references.length} verses for locale: ${locale}`);

  const results = await Promise.all(
    references.map((ref) => fetchVerseWithFallback(ref, locale))
  );

  return results.filter((v): v is FetchedVerse => v !== null);
}
